import { useEffect, useMemo, useState } from "react";
import { type IEntidadesRegistroTarea, type IModalTareaCVProps } from "../interfaces";
import { TareaModelo } from "../../../modelos/Tarea";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import ModalTareaVista from "../vista/modalTareaVista";
import ModeloUsuario from "../../../modelos/Usuario";
import { ClienteModelo } from "../../../modelos/Clientes";
import { TipoTareaModelo } from "../../../modelos/TipoTarea";
import { ModuloModelo } from "../../../modelos/ModulosCheckList";
import { EnumPerfiles } from "../../../globales/enums/enumPerfiles";
import { ContratoModelo } from "../../../modelos/Contratos";
import { EquipoModelo } from "../../../modelos/Equipo";
import { estadoInicialFiltroProyecto } from "../../proyectos/interfaces";
import { EnumTipoTarea } from "../enums/enumTipoTarea";
import { EnumEstadoTarea } from "../enums/enumEstadoTarea";
import ModeloCausalEmergencia from "../../../modelos/CausalEmergencia";
import { EnumEstadoEquipo } from "../../equipos/enums/enumEstadoEquipo";
import { EnumTipoEquipo } from "../../equipos/enums/enumTipoEquipo";
import { estadoInicialFiltroUsuario } from "../../usuarios/interfaces";
import type { ITareaFormulario } from "../interfaces/ITareaFormulario";
import { convertirFormularioATarea } from "../utils/tareaUtils";
import {
    conservaClasificacion,
    esActividadNBSPermitida,
    esProyectoInstalacion,
    mensajeContratoRequerido,
    proyectoAplicaATipoTarea,
    tipoContratoAplicaATipoTarea,
    esTipoTareaPermitido,
    obtenerPerfilesTecnicoPermitidos,
    obtenerPermisosAsignacionTarea,
    type IClasificacionTarea
} from "../utils/permisosTareaUtils";
import { estadoInicialFiltroCliente } from "../../clientes/interfaces";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { obtenerZonaHorariaPorPais, obtenerNombrePaisPorId } from "../utils/zonaHorariaUtils";
import { ModuloTareaModelo } from "../../../modelos/ModulosTarea";

dayjs.extend(utc);
dayjs.extend(timezone);
import { useConfirmacion } from "../../../globales/contextos/confirmacionContext";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import type { IModulo } from "../../../modelos/ModulosCheckList/entidades/IModulo";
import { useAutenticacion } from "../../../globales/contextos/authContext";

const ModalTareaCV: React.FunctionComponent<IModalTareaCVProps> = ({
    consultarTareas,
    abrirModalTarea,
    estadoModal,
    entidadTarea,
    setEntidadTarea,
    visualizacion,
    esEmergencia,
    entidadesRegistro
}) => {
    const modeloTarea = new TareaModelo();
    const modeloProyecto = new ProyectoModelo();
    const modeloEquipo = new EquipoModelo();
    const modeloUsuario = new ModeloUsuario();
    const modeloCliente = new ClienteModelo();
    const modeloTipoTarea = new TipoTareaModelo();
    const modeloModulo = new ModuloModelo();
    const modeloContrato = new ContratoModelo();
    const modeloCausalEmergencia = new ModeloCausalEmergencia();
    const modeloModuloTarea = new ModuloTareaModelo();

    const { mostrarNotificacion } = useNotificaciones();
    const { manejarCargando } = useCargando();
    const { usuario } = useAutenticacion();
    const { confirmar } = useConfirmacion();
    const [errores, setErrores] = useState<Partial<Record<keyof ITareaFormulario, string | null>>>({});
    const [entidadesTarea, setEntidadesTarea] = useState<IEntidadesRegistroTarea>(entidadesRegistro || {
        Equipos: [],
        EquiposTodos: [],
        Proyectos: [],
        ProyectosTodos: [],
        Tecnicos: [],
        TecnicosTodos: [],
        TiposTarea: [],
        ModulosCheckList: [],
        ModulosCheckListTodos: [],
        Clientes: [],
        ClientesTodos: [],
        EstadosTarea: [],
        CausalesEmergencia: [],
        Paises: [],
        HabilitacionesNBS: []
    });

    useEffect(() => {
        if (entidadesRegistro) {
            // Sincronizar catálogos generales desde el padre si existen
            setEntidadesTarea(prev => ({
                ...prev,
                ...entidadesRegistro,
                // Respetar catálogos que se manejan localmente en el modal si es necesario
                Equipos: prev.Equipos.length > 0 ? prev.Equipos : entidadesRegistro.Equipos,
                ModulosCheckList: prev.ModulosCheckList.length > 0 ? prev.ModulosCheckList : entidadesRegistro.ModulosCheckList,
                HabilitacionesNBS: prev.HabilitacionesNBS.length > 0 ? prev.HabilitacionesNBS : entidadesRegistro.HabilitacionesNBS,
            }));
        }
    }, [entidadesRegistro]);

    const [fechaOriginal, setFechaOriginal] = useState<string | null>(null);
    // Clasificación con la que se abrió la tarea en edición: se respeta aunque el usuario no tenga permiso sobre ella
    const [clasificacionOriginal, setClasificacionOriginal] = useState<IClasificacionTarea | null>(null);

    const permisosAsignacion = useMemo(
        () => obtenerPermisosAsignacionTarea(usuario?.Permisos?.map(p => p.Codigo) ?? []),
        [usuario?.Permisos]
    );


    useEffect(() => {
        if (!estadoModal) {
            setErrores({})
            setEntidadesTarea({
                ...entidadesTarea,
                Equipos: [],
                EquiposTodos: []
            })
            setFechaOriginal(null); // Limpiar al cerrar
            setClasificacionOriginal(null);
        } else {
            consultarEntidadesRegistro();
            if (entidadTarea.Id) {
                setFechaOriginal(entidadTarea.FechaInicio); // Guardar fecha inicial al abrir edición
                setClasificacionOriginal({
                    ProyectoId: entidadTarea.ProyectoId,
                    EquipoId: entidadTarea.EquipoId,
                    TipoServicioId: entidadTarea.TipoServicioId,
                    TipoVisitaInstalacionId: entidadTarea.TipoVisitaInstalacionId,
                    AsignadaA: entidadTarea.AsignadaA
                });
            }
        }
    }, [estadoModal])



    useEffect(() => {
        if (esEmergencia) {
            setEntidadTarea({
                ...entidadTarea,
                TipoServicioId: EnumTipoTarea.Emergencia,
                ModulosChecklist: entidadesTarea.ModulosCheckListTodos.filter(m => m.Codigo.toLowerCase() === 'emergencia').map(m => m.Id)
            })
        }

    }, [esEmergencia, entidadesTarea.TiposTarea, estadoModal])

    // ✅ NUEVO useEffect para manejar cambios en TipoServicioId
    useEffect(() => {

        if (entidadTarea.TipoServicioId === EnumTipoTarea.Emergencia) {
            // Si es emergencia, mostrar SOLO los módulos de emergencia
            const modulosEmergencia = entidadesTarea.ModulosCheckListTodos
                .filter(m => m.Codigo.toLowerCase() === 'emergencia');

            setEntidadesTarea(prev => ({
                ...prev,
                ModulosCheckList: modulosEmergencia
            }));
        } else if (entidadTarea.TipoServicioId === EnumTipoTarea.Correctivo || entidadTarea.TipoServicioId === EnumTipoTarea.Acompanamiento) {
            // Si es correctivo, mostrar SOLO los módulos de cliente
            const modulosCliente = entidadesTarea.ModulosCheckListTodos
                .filter(m => m.Codigo === 'MODULO_CLIENTE');

            setEntidadesTarea(prev => ({
                ...prev,
                ModulosCheckList: modulosCliente
            }));
        }

    }, [entidadTarea.TipoServicioId]);

    // ✅ NUEVO useEffect para ajustar el valor inicial de la fecha basado en el proyecto seleccionado
    useEffect(() => {
        if (!entidadTarea.Id && entidadTarea.ProyectoId !== 0 && entidadesTarea.Proyectos.length > 0) {
            const proyecto = entidadesTarea.Proyectos.find(p => p.Id === entidadTarea.ProyectoId);
            const timezoneProyecto = obtenerZonaHorariaPorPais(proyecto?.PaisId);
            const fechaInicioProyecto = dayjs().tz(timezoneProyecto).format("YYYY-MM-DDTHH:mm:ss");

            setEntidadTarea(prev => ({
                ...prev,
                FechaInicio: fechaInicioProyecto,
                FechaFin: prev.Duracion ? dayjs(fechaInicioProyecto).add(prev.Duracion, 'minutes').format("YYYY-MM-DDTHH:mm:ss") : fechaInicioProyecto
            }));
        }
    }, [entidadTarea.ProyectoId, entidadesTarea.Proyectos]);

    useEffect(() => {

        if (!entidadTarea.TipoServicioId) return;
        if (!entidadTarea.CodigoEquipo) return;

        // ✅ Si es emergencia o correctivo, no hacer el filtro por equipo
        if (entidadTarea.TipoServicioId === EnumTipoTarea.Emergencia || entidadTarea.TipoServicioId === EnumTipoTarea.Correctivo || entidadTarea.TipoServicioId === EnumTipoTarea.Acompanamiento) return;

        const cargarModulos = async () => {
            let tipoEquipoId = entidadTarea.Equipo?.TipoEquipoId;

            if (!tipoEquipoId && entidadesTarea.Equipos && entidadesTarea.Equipos.length > 0) {
                const equipoEnLista = entidadesTarea.Equipos.find(e => e.Codigo === entidadTarea.CodigoEquipo);
                tipoEquipoId = equipoEnLista?.TipoEquipoId;
            }

            if (!tipoEquipoId) {
                const equipoRes = await modeloEquipo.obtenerListaEquipos({
                    Codigo: entidadTarea.CodigoEquipo
                });
                tipoEquipoId = equipoRes.Data?.[0]?.TipoEquipoId;
            }

            tipoEquipoId =
                tipoEquipoId === EnumTipoEquipo.Rampa
                    ? EnumTipoEquipo.Escalera
                    : tipoEquipoId;

            const modulosFiltrados = entidadesTarea.ModulosCheckListTodos.filter(modulo => {

                if (modulo.Codigo === 'EMERGENCIA') return false;

                const esModuloCliente = modulo.Codigo === 'MODULO_CLIENTE';
                const coincideEquipo = modulo.TipoEquipo === tipoEquipoId;

                return esModuloCliente || coincideEquipo;

            });

            setEntidadesTarea(prev => ({
                ...prev,
                ModulosCheckList: modulosFiltrados
            }));

        };

        cargarModulos();

    }, [
        entidadTarea.TipoServicioId,
        entidadTarea.CodigoEquipo
    ]);

    // ✅ NUEVO useEffect para NBS: consultar habilitaciones cuando el equipo cambia y la tarea es Visita Instalación
    useEffect(() => {
        if (!entidadTarea.EquipoId) {
            setEntidadesTarea(prev => ({ ...prev, HabilitacionesNBS: [] }));
            return;
        }
        
        if (entidadTarea.TipoServicioId !== EnumTipoTarea.VisitaInstalacion) {
            return;
        }

        const cargarHabilitacionesNBS = async () => {
            try {
                const response = await modeloTarea.consultarHabilitacionVisitaInstalacion(entidadTarea.CodigoEquipo);
                setEntidadesTarea(prev => ({
                    ...prev,
                    HabilitacionesNBS: response.Data || []
                }));
            } catch (error) {
                console.error("Error al cargar habilitaciones NBS", error);
            }
        };

        cargarHabilitacionesNBS();

    }, [entidadTarea.EquipoId, entidadTarea.TipoServicioId]);

    // Filtra Proyectos y Clientes según el Tipo de Tarea (Instalación vs SER). En edición se conserva el proyecto original
    useEffect(() => {
        if (entidadesTarea.TecnicosTodos.length === 0) return;

        const proyectosFiltrados = entidadesTarea.ProyectosTodos.filter(p =>
            p.Id === clasificacionOriginal?.ProyectoId || proyectoAplicaATipoTarea(p, entidadTarea.TipoServicioId)
        );

        const idsClientesSet = new Set(
            proyectosFiltrados
                .map(p => p.ClienteId)
                .filter((id): id is number => id !== null && id !== undefined)
        );
        const clientesFiltrados = entidadesTarea.ClientesTodos.filter(c => idsClientesSet.has(c.Id));

        setEntidadesTarea(prev => ({
            ...prev,
            Proyectos: proyectosFiltrados,
            Clientes: clientesFiltrados
        }));

    }, [entidadTarea.ProyectoId, entidadTarea.TipoServicioId, entidadTarea.TipoVisitaInstalacionId, entidadesTarea.ProyectosTodos, entidadesTarea.HabilitacionesNBS, entidadesTarea.TecnicosTodos, clasificacionOriginal]);

    const mantieneClasificacionOriginal = conservaClasificacion(entidadTarea, clasificacionOriginal);

    // Al crear una tarea sobre un proyecto con contrato vigente de Instalación, solo aplica Visita Instalación
    const restringirAVisitaInstalacion = useMemo(() => {
        if (entidadTarea.Id || esEmergencia || !entidadTarea.ProyectoId) return false;
        const proyecto = entidadesTarea.ProyectosTodos.find(p => p.Id === entidadTarea.ProyectoId);
        return esProyectoInstalacion(proyecto);
    }, [entidadTarea.Id, entidadTarea.ProyectoId, esEmergencia, entidadesTarea.ProyectosTodos]);

    // Tipos de tarea según permisos del usuario. Se conservan el tipo original (edición) y Emergencia en el modal de emergencias
    const tiposTareaDisponibles = useMemo(
        () => entidadesTarea.TiposTarea.filter(t => {
            if (t.Id === clasificacionOriginal?.TipoServicioId) return true;
            if (esEmergencia && t.Id === EnumTipoTarea.Emergencia) return true;
            if (restringirAVisitaInstalacion && t.Id !== EnumTipoTarea.VisitaInstalacion) return false;
            return esTipoTareaPermitido(t.Id, permisosAsignacion);
        }),
        [entidadesTarea.TiposTarea, clasificacionOriginal, esEmergencia, restringirAVisitaInstalacion, permisosAsignacion]
    );

    // Si solo hay un tipo de tarea posible (ej. Planeador: Visita Instalación), se selecciona automáticamente.
    // Al ser Visita Instalación, esto a su vez limita Proyectos y Clientes a contratos de Instalación.
    useEffect(() => {
        if (!estadoModal || entidadTarea.Id || esEmergencia) return;
        if (entidadTarea.TipoServicioId || tiposTareaDisponibles.length !== 1) return;
        manejarCambioValorTarea("TipoServicioId", tiposTareaDisponibles[0].Id);
    }, [estadoModal, entidadTarea.Id, entidadTarea.TipoServicioId, esEmergencia, tiposTareaDisponibles]);

    // Actividades NBS del equipo permitidas al usuario (más la original si no cambió el equipo). No se filtra por Habilitado: se listan todas
    const actividadesNBSDisponibles = useMemo(
        () => entidadesTarea.HabilitacionesNBS.filter(h =>
            (h.TipoVisitaInstalacionId === clasificacionOriginal?.TipoVisitaInstalacionId && entidadTarea.EquipoId === clasificacionOriginal?.EquipoId) ||
            esActividadNBSPermitida(h.Codigo, permisosAsignacion)
        ),
        [entidadesTarea.HabilitacionesNBS, clasificacionOriginal, entidadTarea.EquipoId, permisosAsignacion]
    );

    // Técnicos cuyo perfil corresponde al tipo/actividad y a los permisos del usuario. Se filtran por la regional del
    // proyecto, salvo en Visita Instalación, donde se puede asignar un técnico de cualquier regional
    const tecnicosDisponibles = useMemo(() => {
        const codigoActividad = entidadesTarea.HabilitacionesNBS.find(
            h => h.TipoVisitaInstalacionId === entidadTarea.TipoVisitaInstalacionId
        )?.Codigo;
        const perfiles = obtenerPerfilesTecnicoPermitidos(entidadTarea.TipoServicioId, codigoActividad, permisosAsignacion);
        const regionalId = entidadTarea.TipoServicioId === EnumTipoTarea.VisitaInstalacion
            ? undefined
            : entidadesTarea.ProyectosTodos.find(p => p.Id === entidadTarea.ProyectoId)?.Regional?.Id;

        return entidadesTarea.TecnicosTodos.filter(t =>
            (mantieneClasificacionOriginal && t.Id === clasificacionOriginal?.AsignadaA) ||
            ((!regionalId || t.RegionalId === regionalId) && perfiles.includes(t.PerfilUsuarioId))
        );
    }, [entidadesTarea.TecnicosTodos, entidadesTarea.ProyectosTodos, entidadesTarea.HabilitacionesNBS, entidadTarea.TipoServicioId, entidadTarea.TipoVisitaInstalacionId, entidadTarea.ProyectoId, permisosAsignacion, mantieneClasificacionOriginal, clasificacionOriginal]);

    // Si el técnico asignado deja de ser válido (cambió proyecto, tipo o actividad por cualquier vía), se limpia
    useEffect(() => {
        if (!entidadTarea.AsignadaA || entidadesTarea.TecnicosTodos.length === 0) return;
        // En edición se espera a tener la clasificación original y nunca se quita el técnico original sin cambios
        if (entidadTarea.Id && !clasificacionOriginal) return;
        if (mantieneClasificacionOriginal && entidadTarea.AsignadaA === clasificacionOriginal?.AsignadaA) return;
        if (tecnicosDisponibles.some(t => t.Id === entidadTarea.AsignadaA)) return;
        setEntidadTarea(prev => ({ ...prev, AsignadaA: 0 }));
    }, [tecnicosDisponibles, entidadTarea.AsignadaA]);

    useEffect(() => {
        if (!restringirAVisitaInstalacion) return;
        if (!entidadTarea.TipoServicioId || entidadTarea.TipoServicioId === EnumTipoTarea.VisitaInstalacion) return;

        setEntidadTarea(prev => ({
            ...prev,
            TipoServicioId: 0,
            ModulosChecklist: [],
            CausalEmergenciaId: 0,
            AsignadaA: 0
        }));
        mostrarNotificacion(
            EnumTipoNotificacion.Warning,
            "Tipo de tarea no válido",
            "El contrato vigente es de Instalación, por lo que solo se permite crear tareas de Visita Instalación.",
            6000
        );
    }, [restringirAVisitaInstalacion, entidadTarea.TipoServicioId]);

    const consultarEntidadesRegistro = async () => {
        try {
            manejarCargando(true, undefined, true);
            const [clientes, proyectos, tiposTarea, tecnicos, modulos, causalesEmergencia] = await Promise.all([
                modeloCliente.obtenerListaClientes(estadoInicialFiltroCliente),
                modeloProyecto.obtenerListaProyectos(estadoInicialFiltroProyecto.NitProyecto),
                modeloTipoTarea.obtenerListaTiposTarea(),
                modeloUsuario.obtenerListaUsuarios(estadoInicialFiltroUsuario),
                modeloModulo.obtenerListaModulos(),
                modeloCausalEmergencia.obtenerListaCausalesEmergencia()
            ]);

            const idsClientesSet = new Set(
                proyectos.Data
                    .map(p => p.ClienteId)
                    .filter((id): id is number => id !== null)
            );

            // Filtramos y eliminamos duplicados por Id
            const clientesFiltrados = Array.from(
                new Map(
                    clientes.Data
                        .filter(c => idsClientesSet.has(c.Id))
                        .map(c => [c.Id, c]) // clave -> Id del cliente
                ).values()
            );

            let modulosFiltrados: IModulo[] = modulos.Data.Modulos;

            if (entidadTarea.Id && entidadTarea.Equipo && entidadTarea.Equipo.TipoEquipoId && entidadTarea.TipoServicioId !== EnumTipoTarea.Emergencia && entidadTarea.TipoServicioId !== EnumTipoTarea.Correctivo && entidadTarea.TipoServicioId !== EnumTipoTarea.Acompanamiento) {
                // 1. Definimos qué equipo estamos buscando
                const tipoEquipoRelacionados = entidadTarea.Equipo.TipoEquipoId === EnumTipoEquipo.Rampa
                    ? EnumTipoEquipo.Escalera
                    : entidadTarea.Equipo.TipoEquipoId;

                // 2. Aplicamos el filtro inteligente
                modulosFiltrados = entidadesTarea.ModulosCheckListTodos.filter(modulo => {
                    // REGLA 1: Si es el módulo de EMERGENCIA, nunca se debe listar (false)
                    // Cambia 'Emergencia' por el ID o nombre exacto que tenga en tu base de datos
                    if (modulo.Codigo === 'EMERGENCIA') {
                        return false;
                    }

                    // REGLA 2: Si es el módulo de CLIENTE, siempre se debe listar (true)
                    const esModuloCliente = modulo.Codigo === 'MODULO_CLIENTE';

                    // REGLA 3: Si coincide con el tipo de equipo seleccionado (true)
                    const coincideConEquipo = modulo.TipoEquipo === tipoEquipoRelacionados;

                    return esModuloCliente || coincideConEquipo;
                });
            }

            // Técnicos activos asignables; el filtro por regional, tipo de tarea y permisos se hace en tecnicosDisponibles
            const perfilesPermitidos = [EnumPerfiles.Tecnico, EnumPerfiles.TecnicoNBS, EnumPerfiles.VerificadorCalidad];
            const tecnicosActivos = tecnicos.Data.filter(u => perfilesPermitidos.includes(u.PerfilUsuarioId) && u.Activo);

            setEntidadesTarea(prev => ({
                ...prev,
                Proyectos: proyectos.Data.filter(p => p.Activo && p.Eliminado === false),
                ProyectosTodos: proyectos.Data.filter(p => p.Activo && p.Eliminado === false),
                Clientes: clientesFiltrados,
                ClientesTodos: clientesFiltrados,
                TecnicosTodos: tecnicosActivos,
                TiposTarea: tiposTarea.Data,
                ModulosCheckList: modulosFiltrados,
                ModulosCheckListTodos: modulos.Data.Modulos,
                CausalesEmergencia: causalesEmergencia.Data,
            }));

        } catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al cargar datos de catalogos',
                'No fue posible cargar los datos necesarios para los catalogos. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    }

    const validarCamposTarea = (edicion: boolean) => {
        let esValido = true;
        let fechaValida = true;
        let validacionTipoEquipoTipoTarea = true;
        let validacionPerfilUsuarioLoggeado = true

        // Obtenemos el timezone del proyecto seleccionado
        const proyectoSeleccionado = entidadesTarea.Proyectos.find(p => p.Id === entidadTarea.ProyectoId);
        const timezoneProyecto = obtenerZonaHorariaPorPais(proyectoSeleccionado?.PaisId);
        const nombrePais = obtenerNombrePaisPorId(proyectoSeleccionado?.PaisId);

        // Calculamos 'ahora' usando el timezone del proyecto (generando un string y analizándolo localmente)
        const ahoraString = dayjs().tz(timezoneProyecto).format("YYYY-MM-DDTHH:mm:ss");
        const ahora = dayjs(ahoraString);

        // La fecha seleccionada representa la hora local en el contexto del proyecto
        const inicio = dayjs(entidadTarea.FechaInicio);

        if (!inicio.isValid()) {
            agregarError("FechaInicio", "La fecha de inicio no es válida");
            fechaValida = false;
        } else {
            // Caso 1: Nueva Tarea (No tiene ID) -> Siempre validar contra hoy
            if (!edicion) {
                if (inicio.isBefore(ahora, 'day')) {
                    agregarError("FechaInicio", `La fecha de inicio debe ser mayor o igual a la fecha actual en ${nombrePais}`);
                    fechaValida = false;
                } else if (inicio.isSame(ahora, 'day') && inicio.isBefore(ahora, 'minute')) {
                    agregarError("FechaInicio", `La hora de inicio no puede ser anterior a la hora actual en ${nombrePais}`);
                    fechaValida = false;
                } else {
                    agregarError("FechaInicio", "");
                    fechaValida = true;
                }
            } 
            // Caso 2: Edición -> Solo validar si la fecha cambió respecto a la original
            else {
                const cambioFecha = entidadTarea.FechaInicio !== fechaOriginal;
                
                if (cambioFecha) {
                    if (inicio.isBefore(ahora, 'day')) {
                        agregarError("FechaInicio", `Si vas a cambiar la fecha, debe ser mayor o igual a la fecha actual en ${nombrePais}`);
                        fechaValida = false;
                    } else if (inicio.isSame(ahora, 'day') && inicio.isBefore(ahora, 'minute')) {
                        agregarError("FechaInicio", `La hora de inicio no puede ser anterior a la hora actual en ${nombrePais}`);
                        fechaValida = false;
                    } else {
                        agregarError("FechaInicio", "");
                        fechaValida = true;
                    }
                } else {
                    // Si no cambió, es válida aunque sea del pasado (para no bloquear edición de otros campos)
                    agregarError("FechaInicio", "");
                    fechaValida = true;
                }
            }
        }


        if (entidadTarea.ProyectoId === 0) {
            agregarError("ProyectoId", "Selecciona un proyecto");
            esValido = false;
        }

        if (entidadTarea.ClienteId === 0) {
            agregarError("ClienteId", "Este campo es requerido");
            esValido = false;
        }

        if (entidadTarea.EquipoId === 0) {
            agregarError("EquipoId", "Este campo es requerido");
            esValido = false;
        }

        if (entidadTarea.NumeroContrato === '') {
            agregarError("NumeroContrato", "Este campo es requerido");
            esValido = false;
        }

        if (entidadTarea.TipoServicioId === 0) {
            agregarError("TipoServicioId", "Selecciona un tipo");
            esValido = false;
        } else if (entidadTarea.TipoServicioId === EnumTipoTarea.VisitaInstalacion && !entidadTarea.TipoVisitaInstalacionId) {
            agregarError("TipoVisitaInstalacionId", "Selecciona una actividad NBS");
            esValido = false;
        }

        if (entidadTarea.AsignadaA === 0) {
            agregarError("AsignadaA", "Selecciona un técnico");
            esValido = false;
        }

        if (entidadTarea.Duracion === 0) {
            agregarError("Duracion", "Este campo es requerido");
            esValido = false;
        }

        if (entidadTarea.ModulosChecklist && entidadTarea.ModulosChecklist.length === 0 && entidadTarea.TipoServicioId !== EnumTipoTarea.Emergencia && entidadTarea.TipoServicioId !== EnumTipoTarea.Correctivo && entidadTarea.TipoServicioId !== EnumTipoTarea.Acompanamiento && entidadTarea.TipoServicioId !== EnumTipoTarea.VisitaInstalacion) {
            agregarError("ModulosChecklist", "Selecciona minimo un modulo");
            esValido = false;
        }

        if (entidadTarea.CausalEmergenciaId === 0 && entidadTarea.TipoServicioId === EnumTipoTarea.Emergencia) {
            agregarError("CausalEmergenciaId", "Selecciona una causal de emergencia");
            esValido = false;
        }

        // Permisos del usuario: solo se valida lo que cambió respecto a la tarea original (en edición)
        if (!mantieneClasificacionOriginal) {
            const codigoActividad = entidadesTarea.HabilitacionesNBS.find(
                h => h.TipoVisitaInstalacionId === entidadTarea.TipoVisitaInstalacionId
            )?.Codigo;

            if (entidadTarea.TipoServicioId && !tiposTareaDisponibles.some(t => t.Id === entidadTarea.TipoServicioId)) {
                agregarError("TipoServicioId", "No tienes permiso para crear este tipo de tarea");
                validacionPerfilUsuarioLoggeado = false;
            } else if (entidadTarea.TipoServicioId === EnumTipoTarea.VisitaInstalacion && codigoActividad && !esActividadNBSPermitida(codigoActividad, permisosAsignacion)) {
                agregarError("TipoVisitaInstalacionId", "No tienes permiso para programar esta actividad");
                validacionPerfilUsuarioLoggeado = false;
            }
        }

        const conservaTecnicoOriginal = mantieneClasificacionOriginal && entidadTarea.AsignadaA === clasificacionOriginal?.AsignadaA;
        if (entidadTarea.AsignadaA && !conservaTecnicoOriginal && !tecnicosDisponibles.some(t => t.Id === entidadTarea.AsignadaA)) {
            agregarError("AsignadaA", "No tienes permiso para asignar la tarea a este técnico");
            validacionPerfilUsuarioLoggeado = false;
        }

        if (!validacionPerfilUsuarioLoggeado) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Permisos insuficientes",
                "El tipo de tarea, la actividad o el técnico seleccionado no corresponden a tus permisos.",
                5000
            );
        }

        const equipo = entidadesTarea.Equipos.find(e => e.Id === entidadTarea.EquipoId);
        if (equipo?.EstadoEquipoId === EnumEstadoEquipo.EquipoEnMora && entidadTarea.TipoServicioId !== EnumTipoTarea.Emergencia && entidadTarea.TipoServicioId !== EnumTipoTarea.Correctivo && entidadTarea.TipoServicioId !== EnumTipoTarea.Acompanamiento) {
            validacionTipoEquipoTipoTarea = false;
        }



        if (!esValido) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campos requeridos (*)",
                "Por favor completa todos los campos antes de continuar.",
                5000
            );
        }

        if (!fechaValida) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Fecha Invalida",
                "Selecciona una fecha y hora iguales o posteriores a la actual.",
                5000
            );
        }

        if (!validacionTipoEquipoTipoTarea) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Advertencia",
                "El equipo seleccionado se encuentra en mora, solo se pueden crear tareas de tipo \"Emergencia\", \"Correctivo\" o \"Acompañamiento\".",
                5000
            )
        }



        return esValido && fechaValida && validacionTipoEquipoTipoTarea && validacionPerfilUsuarioLoggeado;
    };


    // Secciones a enviar al backend: solo cuando la actividad NBS se divide en secciones (vacío = todas → null).
    // Si la actividad no está en las habilitaciones cargadas se conserva el valor para no perder la selección guardada.
    const obtenerSeccionesChecklist = (): number[] | null => {
        if (entidadTarea.TipoServicioId !== EnumTipoTarea.VisitaInstalacion || !entidadTarea.TipoVisitaInstalacionId) return null;
        const actividad = entidadesTarea.HabilitacionesNBS.find(
            h => h.TipoVisitaInstalacionId === entidadTarea.TipoVisitaInstalacionId
        );
        if (actividad && !actividad.Secciones?.length) return null;
        return entidadTarea.SeccionesChecklist?.length ? entidadTarea.SeccionesChecklist : null;
    };

    const crearTarea = async () => {

        try {
            manejarCargando(true, undefined, true);
            const tareaParaCrear = {
                ...entidadTarea,
                Id: undefined,
                ModulosChecklist: undefined,
                EstadoTareaId: EnumEstadoTarea.Creada,
                CreadaPor: usuario?.Id || 0,
                CausalEmergenciaId: entidadTarea.CausalEmergenciaId === 0 ? null : entidadTarea.CausalEmergenciaId,
                SeccionesChecklist: obtenerSeccionesChecklist()
            };
            const tareaRespuesta = await modeloTarea.crearTarea(convertirFormularioATarea(tareaParaCrear));
            // En errores de negocio el backend responde Data = null; se deja seguir para mostrar Messages
            const tareaId = tareaRespuesta.Data?.Id;
            if (tareaRespuesta.NotificationType === EnumTipoNotificacion.Success && tareaRespuesta.Data && tareaId && entidadTarea.ModulosChecklist) {
                const respuesta = await modeloModuloTarea.crearModulosTarea({
                    IdTarea: tareaId,
                    IdModuloCatalogos: entidadTarea.ModulosChecklist
                })
                if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
                    abrirModalTarea(false, null);
                } else {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Error,
                        'Error al asociar modulos a la tarea',
                        'La tarea se creo correctamente, pero no se pudieron asociar los módulos. Por favor verifica tu conexión e intenta nuevamente.',
                        5000
                    );
                }
                consultarTareas()
                setEntidadTarea({ ...entidadTarea, Id: tareaId })
            }
            if (tareaRespuesta.Messages.length > 0) {
                tareaRespuesta.Messages.forEach(mensaje =>
                    mostrarNotificacion(
                        tareaRespuesta.NotificationType,
                        '',
                        mensaje,
                        4000
                    ));
            }

        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al crear tarea',
                'No fue posible crear el tarea. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    }

    const editarTarea = async () => {
        manejarCargando(true, undefined, true);

        try {
            // 1️⃣ Editar tarea
            const tareaRespuesta = await modeloTarea.editarTarea({
                ...entidadTarea,
                Equipo: undefined,
                TipoTarea: undefined,
                UsuarioAsignado: undefined,
                ModulosChecklist: [],
                // Se reenvía siempre: Actualizar sin el campo deja la tarea con todas las secciones
                SeccionesChecklist: obtenerSeccionesChecklist()
            });

            if (tareaRespuesta.Messages?.length) {
                tareaRespuesta.Messages.forEach(mensaje =>
                    mostrarNotificacion(
                        tareaRespuesta.NotificationType,
                        '',
                        mensaje,
                        4000
                    )
                );
            }

            if (tareaRespuesta.NotificationType !== EnumTipoNotificacion.Success) {
                return;
            }

            // 2️⃣ Obtener módulos de la tarea
            let modulosTarea;
            try {
                modulosTarea = await modeloModuloTarea.obtenerListaModulosTarea(entidadTarea.Id!);
            } catch {
                mostrarNotificacion(
                    EnumTipoNotificacion.Error,
                    'Error al consultar módulos',
                    'No fue posible obtener los módulos de la tarea.',
                    5000
                );
                return;
            }

            // 3️⃣ Asociar / actualizar módulos
            try {
                let respuesta;

                if (modulosTarea.Data?.length && entidadTarea.ModulosChecklist) {
                    respuesta = await modeloModuloTarea.actualizarModulosTarea({
                        Id: modulosTarea.Data[0].Id,
                        IdTarea: entidadTarea.Id!,
                        IdModuloCatalogos: entidadTarea.ModulosChecklist
                    });
                } else {
                    respuesta = await modeloModuloTarea.crearModulosTarea({
                        IdTarea: entidadTarea.Id!,
                        IdModuloCatalogos: entidadTarea.ModulosChecklist || []
                    });
                }

                if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
                    abrirModalTarea(false, null);
                } else {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Error,
                        'Error al asociar módulos',
                        'La tarea se actualizó correctamente, pero no se pudieron asociar los módulos.',
                        5000
                    );
                }
            } catch {
                mostrarNotificacion(
                    EnumTipoNotificacion.Error,
                    'Error al asociar módulos',
                    'Ocurrió un error al asociar los módulos a la tarea.',
                    5000
                );
            }

            // 4️⃣ Refrescar tareas
            consultarTareas();

        } catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al actualizar tarea',
                'No fue posible actualizar la tarea. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };


    const guardarEditarTarea = async () => {
        if (!entidadTarea.Id) {
            if (!validarCamposTarea(false)) return;
            const confirmado = await confirmar({
                titulo: "Crear tarea",
                mensaje: `¿Está seguro de que desea crear la tarea?`,
                textoAceptar: "Sí, crear tarea",
                textoCancelar: "Cancelar",
                tipo: EnumTipoNotificacion.Warning,
                opcionCancelar: true
            });
            if (confirmado) {
                crearTarea();
            }
        }
        else {
            if (!validarCamposTarea(true)) return;
            const confirmado = await confirmar({
                titulo: "Editar tarea",
                mensaje: `¿Está seguro de que desea editar la tarea #${entidadTarea.Id}?`,
                textoAceptar: "Sí, editar tarea",
                textoCancelar: "Cancelar",
                tipo: EnumTipoNotificacion.Warning,
                opcionCancelar: true
            });

            if (confirmado) {
                editarTarea();
            }
        }
    }


    const manejarCambioValorTarea = async (
        campo: keyof ITareaFormulario,
        valor: ITareaFormulario[keyof ITareaFormulario]
    ) => {

        let efectos: (() => void | Promise<void>)[] = [];

        if (["ProyectoId", "ClienteId", "NumeroContrato", "CodigoEquipo", "EquipoId"].includes(campo as string)) {
            efectos.push(() => {
                agregarError("FechaInicio", "");
            });
        }

        const efectosBase = [...efectos];

        const calcularNuevoEstado = (prevEstado: ITareaFormulario) => {
            efectos = [...efectosBase];

            let nuevoEstado: ITareaFormulario = { ...prevEstado, [campo]: valor };

            const esEmergencia =
                nuevoEstado.TipoServicioId === EnumTipoTarea.Emergencia;

            const modulosEmergencia = (entidadesTarea.ModulosCheckListTodos ?? [])
                .filter(m => m.Codigo.toLowerCase() === "emergencia")
                .map(m => m.Id);

            const esCorrectivo =
                nuevoEstado.TipoServicioId === EnumTipoTarea.Correctivo ||
                nuevoEstado.TipoServicioId === EnumTipoTarea.Acompanamiento;

            const modulosCliente = (entidadesTarea.ModulosCheckListTodos ?? [])
                .filter(m => m.Codigo === "MODULO_CLIENTE")
                .map(m => m.Id);

            // limpiarEquipos: solo cuando se desvincula el proyecto; si el proyecto se conserva, sus equipos siguen aplicando
            const restaurarListas = (limpiarEquipos = true) => {
                efectos.push(() => {
                    setEntidadesTarea(prev => {
                        const proyectosFiltrados = (prev.ProyectosTodos ?? prev.Proyectos)
                            .filter(p => proyectoAplicaATipoTarea(p, nuevoEstado.TipoServicioId));

                        const idsClientesSet = new Set(
                            proyectosFiltrados
                                .map(p => p.ClienteId)
                                .filter((id): id is number => id !== null && id !== undefined)
                        );
                        const clientesFiltrados = (prev.ClientesTodos ?? prev.Clientes).filter(c => idsClientesSet.has(c.Id));

                        return {
                            ...prev,
                            Proyectos: proyectosFiltrados,
                            Clientes: clientesFiltrados,
                            Equipos: limpiarEquipos ? [] : prev.Equipos
                        };
                    });
                });
            };

            /* ===============================
               CAMBIO PROYECTO
            =================================*/

            if (campo === "ProyectoId") {

                const proyectoId = Number(valor);

                if (!proyectoId) {

                    restaurarListas();

                    return {
                        ...nuevoEstado,
                        ProyectoId: 0,
                        ClienteId: 0,
                        ContratoId: 0,
                        NumeroContrato: "",
                        EquipoId: 0,
                        ResponsableId: 0,
                        CodigoEquipo: "",
                        ModulosChecklist: esEmergencia ? modulosEmergencia : (esCorrectivo ? modulosCliente : [])
                    };
                }

                const proyecto = entidadesTarea.Proyectos.find(
                    p => p.Id === proyectoId
                );

                if (!proyecto) return nuevoEstado;

                nuevoEstado = {
                    ...nuevoEstado,
                    ClienteId: proyecto.ClienteId ?? 0,
                    NumeroContrato: proyecto.Contrato?.Numero || "",
                    EquipoId: 0,
                    CodigoEquipo: "",
                    AsignadaA: 0,
                    ModulosChecklist: esEmergencia ? modulosEmergencia : (esCorrectivo ? modulosCliente : [])
                };

                efectos.push(async () => {

                    if (!proyecto.ClienteId) {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            "No hay un cliente asociado al proyecto",
                            "Por favor verifica el proyecto",
                            6000
                        );
                    }

                    if (!proyecto.ContratoId) {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            "No hay un contrato asociado al proyecto",
                            "Por favor verifica el proyecto",
                            6000
                        );
                    }

                    const equiposRelacionados =
                        await consultarEquiposPorProyectoId(proyecto.Id);

                    const equiposFiltrados = equiposRelacionados.filter(
                        e =>
                            e.EstadoEquipoId !== EnumEstadoEquipo.SinContrato &&
                            e.EstadoEquipoId !== EnumEstadoEquipo.Suspendido
                    );

                    setEntidadesTarea(prev => ({
                        ...prev,
                        Equipos: equiposFiltrados.length > 1 ? equiposFiltrados : []
                    }));

                    if (equiposFiltrados.length === 1) {

                        const equipo = equiposFiltrados[0];

                        setEntidadTarea(prev => ({
                            ...prev,
                            Equipo: equipo,
                            EquipoId: equipo.Id,
                            CodigoEquipo: equipo.Codigo
                        }));

                    } else if (equiposFiltrados.length === 0) {
                        setEntidadTarea(prev => ({
                            ...prev,
                            Equipo: undefined,
                            EquipoId: 0,
                            CodigoEquipo: ""
                        }));
                    }

                });

                return nuevoEstado;
            }

            /* ===============================
               CAMBIO CLIENTE
            =================================*/

            if (campo === "ClienteId") {

                const clienteId = Number(valor);

                if (!clienteId) {

                    restaurarListas();

                    return {
                        ...nuevoEstado,
                        ClienteId: 0,
                        ProyectoId: 0,
                        ContratoId: 0,
                        NumeroContrato: "",
                        CodigoEquipo: "",
                        EquipoId: 0,
                        ResponsableId: 0,
                        ModulosChecklist: esEmergencia ? modulosEmergencia : (esCorrectivo ? modulosCliente : [])
                    };
                }

                efectos.push(() => {

                    const proyectos = (entidadesTarea.ProyectosTodos ?? [])
                        .filter(p => p.ClienteId === clienteId);

                    setEntidadesTarea(prev => ({
                        ...prev,
                        Proyectos: proyectos
                    }));

                    if (proyectos.length === 0) {

                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            "No hay proyectos asociados al cliente",
                            "Por favor verifica",
                            6000
                        );

                    }

                });

                return nuevoEstado;
            }

            /* ===============================
               CAMBIO EQUIPO
            =================================*/

            if (campo === "EquipoId") {

                const equipo = entidadesTarea.Equipos
                    .find(e => e.Id === Number(valor));

                if (!equipo) {

                    return {
                        ...nuevoEstado,
                        EquipoId: 0,
                        TipoVisitaInstalacionId: null,
                        AsignadaA: 0,
                        ModulosChecklist: esEmergencia ? modulosEmergencia : (esCorrectivo ? modulosCliente : [])
                    };
                }

                nuevoEstado = {
                    ...nuevoEstado,
                    Equipo: equipo,
                    TipoVisitaInstalacionId: null,
                    AsignadaA: 0,
                    ModulosChecklist: esEmergencia ? modulosEmergencia.map(m => m) : (esCorrectivo ? modulosCliente.map(m => m) : []),
                    CodigoEquipo: equipo.Codigo
                };

                efectos.push(() => {

                    if (!esEmergencia && !esCorrectivo) {
                        const tipoEquipo =
                            equipo.TipoEquipoId === EnumTipoEquipo.Rampa
                                ? EnumTipoEquipo.Escalera
                                : equipo.TipoEquipoId;

                        const modulosFiltrados =
                            entidadesTarea.ModulosCheckListTodos.filter(m => {

                                if (m.Codigo === "EMERGENCIA") return false;

                                const esCliente = m.Codigo === "MODULO_CLIENTE";
                                const coincideEquipo =
                                    m.TipoEquipo === tipoEquipo;

                                return esCliente || coincideEquipo;

                            });

                        setEntidadesTarea(prev => ({
                            ...prev,
                            ModulosCheckList: modulosFiltrados
                        }));

                    }

                });

                return nuevoEstado;
            }

            /* ===============================
               CAMBIO TIPO SERVICIO
            =================================*/

            if (campo === "TipoServicioId") {

                const tipoId = Number(valor);

                if (!tipoId) {
                    restaurarListas(false);
                    return {
                        ...nuevoEstado,
                        TipoServicioId: 0,
                        ModulosChecklist: [],
                        AsignadaA: 0
                    };
                }

                // Actualizar módulos según el tipo de tarea
                if (tipoId === EnumTipoTarea.Emergencia) {
                    nuevoEstado = {
                        ...nuevoEstado,
                        ModulosChecklist: modulosEmergencia
                    };
                } else if (tipoId === EnumTipoTarea.Correctivo || tipoId === EnumTipoTarea.Acompanamiento) {
                    nuevoEstado = {
                        ...nuevoEstado,
                        ModulosChecklist: modulosCliente
                    };
                } else {
                    nuevoEstado = {
                        ...nuevoEstado,
                        ModulosChecklist: []
                    };
                }

                // Limpiar campos específicos según el tipo y siempre limpiar Técnico
                if (tipoId !== EnumTipoTarea.VisitaInstalacion) {
                    nuevoEstado.TipoVisitaInstalacionId = null;
                }
                if (tipoId !== EnumTipoTarea.Emergencia) {
                    nuevoEstado.CausalEmergenciaId = 0;
                }
                nuevoEstado.AsignadaA = 0;

                // Verificar si el proyecto actual sigue siendo válido
                let proyectoInvalido = false;
                if (nuevoEstado.ProyectoId) {
                    const proyectosBase = entidadesTarea.ProyectosTodos ?? entidadesTarea.Proyectos;
                    const proyectoActual = proyectosBase.find(p => p.Id === nuevoEstado.ProyectoId);
                    proyectoInvalido = !!proyectoActual && !proyectoAplicaATipoTarea(proyectoActual, tipoId);
                }

                if (proyectoInvalido) {
                    nuevoEstado = {
                        ...nuevoEstado,
                        ProyectoId: 0,
                        ClienteId: 0,
                        NumeroContrato: "",
                        EquipoId: 0,
                        CodigoEquipo: "",
                        AsignadaA: 0,
                        TipoVisitaInstalacionId: null
                    };

                    efectos.push(() => {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            "Proyecto removido",
                            `El proyecto seleccionado no es válido para este tipo de tarea (${mensajeContratoRequerido(tipoId)}), por lo que fue desvinculado.`,
                            6000
                        );
                    });
                }

                // Actualizar las listas (Proyectos, Clientes) según el nuevo tipo de tarea
                restaurarListas(proyectoInvalido);

                return nuevoEstado;
            }

            return nuevoEstado;

        };

        setEntidadTarea(prevEstado => {
            const nuevoEstado = calcularNuevoEstado(prevEstado);
            // Las secciones elegidas dependen del equipo y la actividad NBS: si alguno cambia, se limpian
            const cambioEquipoOActividad =
                nuevoEstado.EquipoId !== prevEstado.EquipoId ||
                (nuevoEstado.TipoVisitaInstalacionId ?? null) !== (prevEstado.TipoVisitaInstalacionId ?? null);
            return cambioEquipoOActividad && campo !== "SeccionesChecklist"
                ? { ...nuevoEstado, SeccionesChecklist: null }
                : nuevoEstado;
        });

        /* ==================================
           EJECUTAR EFECTOS
        ================================== */

        for (const efecto of efectos) {
            await efecto();
        }

    };




    const agregarError = async (campo: keyof ITareaFormulario, error: string) => {
        setErrores((prevErrores) => ({
            ...prevErrores,
            [campo]: error
        }));

    }

    const consultarPorNumeroContrato = async (numero: string) => {
        try {
            manejarCargando(true, undefined, true);
            const contrato = await modeloContrato.obtenerContratoFiltros({
                NumeroContrato: numero,
                Activo: true,
                FechaFin: null,
                FechaInicio: null,
                Id: null
            });
            if (contrato.Messages.length > 0) {
                contrato.Messages.forEach(mensaje =>
                    mostrarNotificacion(
                        contrato.NotificationType,
                        '',
                        mensaje,
                        4000
                    ));
            }
            if (contrato.Data) {
                if (!tipoContratoAplicaATipoTarea(contrato.Data.TipoContratoId, entidadTarea.TipoServicioId)) {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Warning,
                        'Contrato no válido para la tarea',
                        `El contrato ingresado existe, pero no aplica para este tipo de tarea (${mensajeContratoRequerido(entidadTarea.TipoServicioId)}).`,
                        5000
                    );
                    return;
                }
                
                if (contrato.Data.ProyectoId) {
                    const proyecto = contrato ? entidadesTarea.Proyectos.find(p => p.Id === contrato.Data.ProyectoId) : undefined
                    if (proyecto) {
                        setEntidadTarea({
                            ...entidadTarea,
                            ProyectoId: proyecto ? proyecto.Id : 0,
                            ClienteId: proyecto && proyecto.ClienteId ? proyecto.ClienteId : 0,
                            ModulosChecklist: entidadTarea.TipoServicioId === EnumTipoTarea.Emergencia
                                ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo.toLowerCase() === 'emergencia').map(m => m.Id)
                                : (entidadTarea.TipoServicioId === EnumTipoTarea.Correctivo || entidadTarea.TipoServicioId === EnumTipoTarea.Acompanamiento ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo === 'MODULO_CLIENTE').map(m => m.Id) : [])
                        })

                        consultarEquiposPorProyectoId(proyecto.Id).then((equiposRelacionados) => {
                            const equiposFiltrados = equiposRelacionados.filter(e => e.EstadoEquipoId !== EnumEstadoEquipo.SinContrato && e.EstadoEquipoId !== EnumEstadoEquipo.Suspendido);
                            setEntidadesTarea((prev) => ({
                                ...prev,
                                Equipos: equiposFiltrados.length > 1 ? equiposFiltrados : [],
                            }));

                            if (equiposFiltrados.length === 0) {
                                setEntidadTarea((prev) => ({
                                    ...prev,
                                    EquipoId: 0,
                                    CodigoEquipo: '',
                                }));
                            }

                            // Si hay solo un equipo, seleccionarlo automáticamente
                            if (equiposFiltrados.length === 1) {
                                setEntidadTarea((prev) => ({
                                    ...prev,
                                    Equipo: equiposFiltrados[0],
                                    EquipoId: equiposFiltrados[0].Id,
                                    CodigoEquipo: equiposFiltrados[0].Codigo,
                                    ModulosChecklist: prev.TipoServicioId === EnumTipoTarea.Emergencia
                                        ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo.toLowerCase() === 'emergencia').map(m => m.Id)
                                        : (prev.TipoServicioId === EnumTipoTarea.Correctivo || prev.TipoServicioId === EnumTipoTarea.Acompanamiento ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo === 'MODULO_CLIENTE').map(m => m.Id) : [])
                                }));
                                agregarError("EquipoId", "");
                                // 1. Definimos qué equipo estamos buscando
                                const tipoEquipoRelacionados = equiposFiltrados[0].TipoEquipoId === EnumTipoEquipo.Rampa
                                    ? EnumTipoEquipo.Escalera
                                    : equiposFiltrados[0].TipoEquipoId;

                                // 2. Aplicamos el filtro inteligente
                                const modulosFiltrados = entidadesTarea.ModulosCheckListTodos.filter(modulo => {

                                    // REGLA 1: Si es el módulo de EMERGENCIA, nunca se debe listar (false)
                                    // Cambia 'Emergencia' por el ID o nombre exacto que tenga en tu base de datos
                                    if (modulo.Codigo === 'EMERGENCIA') {
                                        return false;
                                    }

                                    // REGLA 2: Si es el módulo de CLIENTE, siempre se debe listar (true)
                                    const esModuloCliente = modulo.Codigo === 'MODULO_CLIENTE';

                                    // REGLA 3: Si coincide con el tipo de equipo seleccionado (true)
                                    const coincideConEquipo = modulo.TipoEquipo === tipoEquipoRelacionados;

                                    return esModuloCliente || coincideConEquipo;
                                });
                                setEntidadesTarea((prev) => ({
                                    ...prev,
                                    ModulosCheckList: modulosFiltrados,
                                }))
                            }
                        });
                    }
                    else {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            'No hay un proyecto asociado al número de contrato',
                            'Por favor verifica',
                            6000
                        );
                    }



                    if (contrato && contrato.Data.Id) {
                        agregarError('NumeroContrato', '');
                        agregarError('FechaInicio', '');
                    } else {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            'No existe un contrato con el número ingresado',
                            'Por favor verifica',
                            6000)
                    }
                    if (proyecto && proyecto.ClienteId) {
                        agregarError('ClienteId', '')
                    } else {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            'No hay un cliente asociado al proyecto',
                            'Por favor verifica el proyecto',
                            6000)
                    }
                    if (proyecto && proyecto.Id) agregarError('ProyectoId', '')
                }

            }
            else {
                setEntidadTarea((prev) => ({
                    ...prev,
                    ProyectoId: 0,
                    ClienteId: 0,
                    EquipoId: 0,
                    CodigoEquipo: '',
                    ModulosChecklist: entidadTarea.TipoServicioId === EnumTipoTarea.Emergencia
                        ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo.toLowerCase() === 'emergencia').map(m => m.Id)
                        : (entidadTarea.TipoServicioId === EnumTipoTarea.Correctivo || entidadTarea.TipoServicioId === EnumTipoTarea.Acompanamiento ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo === 'MODULO_CLIENTE').map(m => m.Id) : [])
                }));

                setEntidadesTarea((prev) => ({
                    ...prev,
                    Equipos: []
                }));
                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    'El contrato si existe, pero no está asociado a ningún proyecto',
                    'Por favor verifica el número de contrato ingresado o contacta al administrador.',
                    6000
                );
            }
        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al consultar contrato',
                'No fue posible consultar el contrato. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        }
        finally {
            manejarCargando(false);
        }

    }

    const consultarPorCodigoEquipo = async (codigo: string) => {
        try {
            manejarCargando(true, "Buscando equipo...", true);
            const equipos = await modeloEquipo.obtenerListaEquipos({ Codigo: codigo });

            if (equipos.Messages.length > 0) {
                equipos.Messages.forEach(mensaje =>
                    mostrarNotificacion(equipos.NotificationType, '', mensaje, 4000)
                );
            }

            if (equipos.Data && equipos.Data.length > 0) {
                // 1. Tomamos el equipo encontrado
                const equipoEncontrado = equipos.Data[0];

                // 2. Verificamos si el estado es uno de los prohibidos
                const esSuspendido = equipoEncontrado.EstadoEquipoId === EnumEstadoEquipo.Suspendido;
                const esSinContrato = equipoEncontrado.EstadoEquipoId === EnumEstadoEquipo.SinContrato;

                if (esSuspendido || esSinContrato) {
                    const nombreEstado = equipoEncontrado.EstadoEquipo?.Nombre || (esSuspendido ? "SUSPENDIDO" : "SIN CONTRATO");

                    mostrarNotificacion(
                        EnumTipoNotificacion.Warning,
                        'Equipo no apto para tareas',
                        `El equipo con código ${codigo} se encuentra en estado ${nombreEstado.toUpperCase()}. No es posible crear tareas para equipos en este estado.`,
                        6000
                    );
                    setEntidadTarea(prev => ({ ...prev, CodigoEquipo: "" }));
                    return;
                }

                const proyecto = entidadesTarea.Proyectos.find(p => p.Id === equipoEncontrado.ProyectoId);

                if (proyecto) {
                    const equipo = equipoEncontrado;
                    const tipoEquipoRelacionados = equipo.TipoEquipoId === EnumTipoEquipo.Rampa
                        ? EnumTipoEquipo.Escalera
                        : equipo.TipoEquipoId;

                    const modulosFiltrados = entidadesTarea.ModulosCheckListTodos.filter(modulo => {
                        if (modulo.Codigo === 'EMERGENCIA') {
                            return false;
                        }

                        const esModuloCliente = modulo.Codigo === 'MODULO_CLIENTE';
                        const coincideConEquipo = modulo.TipoEquipo === tipoEquipoRelacionados;

                        return esModuloCliente || coincideConEquipo;
                    });

                    setEntidadesTarea((prev) => ({
                        ...prev,
                        ModulosCheckList: modulosFiltrados,
                    }));

                    setEntidadTarea((prev) => ({
                        ...prev,
                        EquipoId: equipo.Id,
                        Equipo: equipo,
                        ProyectoId: proyecto.Id,
                        ClienteId: proyecto.ClienteId || 0,
                        NumeroContrato: proyecto.Contrato?.Numero || '',
                        ModulosChecklist: entidadTarea.TipoServicioId === EnumTipoTarea.Emergencia
                            ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo.toLowerCase() === 'emergencia').map(m => m.Id)
                            : (entidadTarea.TipoServicioId === EnumTipoTarea.Correctivo || entidadTarea.TipoServicioId === EnumTipoTarea.Acompanamiento ? (entidadesTarea.ModulosCheckListTodos ?? []).filter(m => m.Codigo === 'MODULO_CLIENTE').map(m => m.Id) : [])
                    }));

                    setEntidadesTarea((prev) => ({
                        ...prev,
                        Equipos: []
                    }));

                    agregarError('EquipoId', '');
                    agregarError('ClienteId', '');
                    agregarError('ProyectoId', '');
                    agregarError('FechaInicio', '');

                } else {
                    const proyectoEnTodos = entidadesTarea.ProyectosTodos.find(p => p.Id === equipoEncontrado.ProyectoId);
                    
                    if (proyectoEnTodos && !proyectoAplicaATipoTarea(proyectoEnTodos, entidadTarea.TipoServicioId)) {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            'Equipo no válido para la tarea',
                            `El equipo ingresado existe, pero su proyecto no aplica para este tipo de tarea (${mensajeContratoRequerido(entidadTarea.TipoServicioId)}).`,
                            6000
                        );
                    } else {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Warning,
                            'Proyecto no encontrado',
                            `El equipo existe y está activo, pero no pudimos encontrar el proyecto ${equipoEncontrado.ProyectoId} asociado (puede estar inactivo o fuera de su regional).`,
                            6000
                        );
                    }
                    setEntidadTarea(prev => ({ ...prev, CodigoEquipo: "" }));
                }
            } else {
                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    'Equipo no encontrado',
                    'No se encontró ningún equipo con el código ingresado.',
                    5000
                );
                setEntidadTarea(prev => ({ ...prev, CodigoEquipo: "" }));
            }
        }
        catch (error) {
            console.error(error);
            mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', 'Error al consultar el equipo.', 5000);
        }
        finally {
            manejarCargando(false);
        }
    };

    const consultarEquiposPorProyectoId = async (proyectoId: number) => {
        try {
            manejarCargando(true, undefined, true);

            const equipos = await modeloEquipo.obtenerListaEquipos({ ProyectoId: proyectoId });
            if (equipos.Messages.length > 0) {
                const mensajesUnicos = Array.from(new Set(equipos.Messages));
                mensajesUnicos.forEach(mensaje =>
                    mostrarNotificacion(
                        equipos.NotificationType,
                        '',
                        mensaje,
                        4000
                    )
                );
            }

            if (equipos.Data) {
                const equiposFiltrados = equipos.Data.filter(e => e.EstadoEquipoId !== EnumEstadoEquipo.SinContrato && e.EstadoEquipoId !== EnumEstadoEquipo.Suspendido);

                if (equiposFiltrados.length === 0 && equipos.Data.length > 0 && equipos.Messages.length === 0) {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Warning,
                        'Proyecto sin equipos válidos',
                        'El proyecto seleccionado tiene equipos, pero no están en estado válido para crear tareas (pueden estar suspendidos o sin contrato).',
                        6000
                    );
                } else if (equipos.Data.length === 0 && equipos.Messages.length === 0) {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Warning,
                        'Proyecto sin equipos',
                        'El proyecto seleccionado no tiene ningún equipo asociado en el sistema.',
                        6000
                    );
                }

                return equiposFiltrados;
            }

            return [];
        } catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al consultar equipos',
                'No fue posible obtener la lista de equipos por proyecto. Intenta nuevamente.',
                5000
            );
            return [];
        } finally {
            manejarCargando(false);
        }
    };



    return (
        <ModalTareaVista
            abrir={estadoModal}
            abrirModalTarea={abrirModalTarea}
            guardarEditarTarea={guardarEditarTarea}
            manejarCambioValorTarea={manejarCambioValorTarea}
            entidadesRegistro={{
                ...entidadesTarea,
                TiposTarea: tiposTareaDisponibles,
                HabilitacionesNBS: actividadesNBSDisponibles,
                Tecnicos: tecnicosDisponibles
            }}
            agregarError={agregarError}
            errores={errores}
            tareaSeleccionado={entidadTarea}
            consultarPorNumeroContrato={consultarPorNumeroContrato}
            consultarPorCodigoEquipo={consultarPorCodigoEquipo}
            visualizacion={visualizacion}
            tienePermisoNBS={esTipoTareaPermitido(EnumTipoTarea.VisitaInstalacion, permisosAsignacion)}
        />
    )
}

export default ModalTareaCV;